import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Coins, ChartLineUp, Gift, Trophy, ArrowUp, ArrowDown, Star, Crown, Medal, Lightning, Leaf, ArrowsClockwise, Fire, Wallet } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  'Emerging Artisan': '#CD7F32',
  'Rising Artisan': '#C0C0C0',
  'Master Artisan': '#FFD700',
  'Heritage Guardian': '#E5E4E2',
  'Conscious Consumer': '#CD7F32',
  'Sustainability Advocate': '#C0C0C0',
  'Impact Champion': '#FFD700',
  'Earth Guardian': '#E5E4E2',
  'Starter': '#CD7F32',
  'Growth': '#C0C0C0',
  'Scale': '#FFD700',
  'Enterprise': '#E5E4E2',
};

const TIER_ICONS = {
  'Emerging Artisan': Medal,
  'Rising Artisan': Medal,
  'Master Artisan': Crown,
  'Heritage Guardian': Crown,
  'Conscious Consumer': Star,
  'Sustainability Advocate': Star,
  'Impact Champion': Trophy,
  'Earth Guardian': Trophy,
};

const WalletPage = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [redemptionOptions, setRedemptionOptions] = useState([]);
  const [rewardRules, setRewardRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [onChainBalances, setOnChainBalances] = useState(null);
  const [exchangeAmount, setExchangeAmount] = useState('');
  const [retireAmount, setRetireAmount] = useState('');

  // Get user identity from localStorage
  const irisId = localStorage.getItem('ginsgood_iris_id');
  const irisName = localStorage.getItem('ginsgood_iris_name');
  const irisType = localStorage.getItem('ginsgood_iris_type') || 'seller';

  useEffect(() => {
    if (!irisId) {
      toast.error('Please complete iris authentication first');
      navigate('/iris-auth');
      return;
    }
    fetchWalletData();
  }, [irisId, navigate]);

  const fetchWalletData = async () => {
    try {
      const [walletRes, optionsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/gsg/wallet/${irisType}/${irisId}`),
        axios.get(`${API}/gsg/redemption-options`),
        axios.get(`${API}/gsg/leaderboard?limit=5`)
      ]);

      setWallet(walletRes.data.wallet);
      setTierInfo(walletRes.data.tier_info);
      setRewardRules(walletRes.data.reward_rules);
      setRedemptionOptions(optionsRes.data.options);
      setLeaderboard(leaderboardRes.data.leaderboard);

      // Fetch transactions
      if (walletRes.data.wallet?.id) {
        const txRes = await axios.get(`${API}/gsg/wallet/${walletRes.data.wallet.id}/transactions`);
        setTransactions(txRes.data.transactions);
      }

      // Fetch on-chain token balances
      try {
        const tokenRes = await axios.get(`${API}/tokens/user/${irisId}/balance`);
        setOnChainBalances(tokenRes.data);
      } catch (err) {
        console.log('On-chain balances not available:', err);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const setupTokenAccounts = async () => {
    try {
      const res = await axios.post(`${API}/tokens/user/${irisId}/setup`);
      if (res.data.success) {
        toast.success('Token accounts created successfully!');
        fetchWalletData();
      }
    } catch (err) {
      toast.error('Failed to setup token accounts');
    }
  };

  const handleExchange = async () => {
    const amount = parseFloat(exchangeAmount);
    if (isNaN(amount) || amount < 100) {
      toast.error('Minimum exchange is 100 GSG');
      return;
    }
    try {
      const res = await axios.post(`${API}/tokens/exchange?iris_id=${irisId}&gsg_amount=${amount}`);
      if (res.data.success) {
        toast.success(`Exchanged ${amount} GSG for ${res.data.gcc_received} GCC!`);
        setExchangeAmount('');
        fetchWalletData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Exchange failed');
    }
  };

  const handleRetire = async () => {
    const amount = parseFloat(retireAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      const res = await axios.post(`${API}/tokens/retire?iris_id=${irisId}&gcc_amount=${amount}`);
      if (res.data.success) {
        toast.success(`Retired ${amount} GCC - ${amount}kg CO2 offset!`);
        setRetireAmount('');
        fetchWalletData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retirement failed');
    }
  };

  const handleRedeem = async (optionId, cost) => {
    if (!wallet) return;
    
    if (wallet.balance < cost) {
      toast.error(`Insufficient balance. Need ${cost} GSG.`);
      return;
    }

    try {
      const response = await axios.post(`${API}/gsg/wallet/${wallet.id}/redeem?option_id=${optionId}`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchWalletData(); // Refresh data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Redemption failed');
    }
  };

  const getTierProgress = () => {
    if (!tierInfo || !wallet) return 0;
    const currentTierMin = tierInfo.min_lifetime || 0;
    const nextTierMin = tierInfo.max_lifetime || 1;
    if (nextTierMin - currentTierMin === 0) return 100;
    const progress = ((wallet.lifetime_earned - currentTierMin) / (nextTierMin - currentTierMin)) * 100;
    return Math.min(Math.max(progress, 0), 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="animate-pulse">Loading wallet...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const TierIcon = TIER_ICONS[tierInfo?.name] || Star;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge variant="outline" className="mb-4">
              <Coins weight="duotone" className="w-4 h-4 mr-2" />
              GinsGoodCoin Wallet
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {irisName}'s Wallet
            </h1>
            <p className="text-muted-foreground">
              Earn and redeem GSG tokens for sustainable actions
            </p>
          </div>

          {/* Balance Card */}
          <Card className="p-8 mb-8 bg-gradient-to-br from-[#2C3E2D] to-[#4A6C4C] text-white">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Balance */}
              <div className="text-center md:text-left">
                <p className="text-sm opacity-80 mb-1">Current Balance</p>
                <div className="flex items-center gap-2 justify-center md:justify-start">
                  <Coins weight="fill" className="w-8 h-8 text-yellow-400" />
                  <span className="text-4xl font-bold">{wallet?.balance?.toLocaleString() || 0}</span>
                  <span className="text-xl opacity-80">GSG</span>
                </div>
              </div>

              {/* Tier */}
              <div className="text-center">
                <p className="text-sm opacity-80 mb-1">Current Tier</p>
                <div className="flex items-center gap-2 justify-center">
                  <TierIcon weight="fill" className="w-6 h-6" style={{ color: TIER_COLORS[tierInfo?.name] || '#FFD700' }} />
                  <span className="text-2xl font-medium">{tierInfo?.name}</span>
                </div>
                <div className="mt-2">
                  <Progress value={getTierProgress()} className="h-2 bg-white/20" />
                  <p className="text-xs mt-1 opacity-70">
                    {wallet?.lifetime_earned?.toLocaleString()} / {tierInfo?.max_lifetime?.toLocaleString()} GSG to next tier
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="text-center md:text-right">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs opacity-70">Lifetime Earned</p>
                    <p className="text-lg font-semibold flex items-center justify-center md:justify-end gap-1">
                      <ChartLineUp className="w-4 h-4 text-green-400" />
                      {wallet?.lifetime_earned?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs opacity-70">Total Spent</p>
                    <p className="text-lg font-semibold flex items-center justify-center md:justify-end gap-1">
                      <Gift className="w-4 h-4 text-pink-400" />
                      {wallet?.lifetime_spent?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tier Perks */}
            {tierInfo?.perks && tierInfo.perks.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-sm opacity-80 mb-2">Your Perks:</p>
                <div className="flex flex-wrap gap-2">
                  {tierInfo.perks.map((perk) => (
                    <Badge key={perk} variant="secondary" className="bg-white/20 text-white border-0">
                      {perk}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Tabs defaultValue="earn" className="space-y-6">
            <TabsList className="grid w-full max-w-lg mx-auto grid-cols-5">
              <TabsTrigger value="earn" data-testid="tab-earn">Earn</TabsTrigger>
              <TabsTrigger value="carbon" data-testid="tab-carbon">Carbon</TabsTrigger>
              <TabsTrigger value="redeem" data-testid="tab-redeem">Redeem</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
              <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaders</TabsTrigger>
            </TabsList>

            {/* Earn Tab */}
            <TabsContent value="earn">
              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Lightning weight="duotone" className="w-5 h-5 text-yellow-500" />
                  Ways to Earn GSG
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(rewardRules || {}).map(([key, rule]) => (
                    <div 
                      key={key} 
                      className="p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors flex justify-between items-center"
                      data-testid={`reward-${key}`}
                    >
                      <div>
                        <p className="font-medium">{rule.description}</p>
                        <p className="text-xs text-muted-foreground">{key.replace(/_/g, ' ')}</p>
                      </div>
                      <Badge className="bg-[#4A6C4C]">+{rule.amount} GSG</Badge>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => navigate('/certify')} className="gap-2">
                    <Coins className="w-4 h-4" />
                    Certify a Product to Earn
                  </Button>
                  <Button onClick={() => navigate('/referral')} variant="outline" className="gap-2">
                    <Gift className="w-4 h-4" />
                    Refer & Earn
                  </Button>
                </div>
              </Card>
            </TabsContent>

            {/* Carbon Credits Tab */}
            <TabsContent value="carbon">
              <div className="space-y-6">
                {/* On-Chain Balances Card */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium flex items-center gap-2">
                      <Wallet weight="duotone" className="w-5 h-5 text-green-500" />
                      On-Chain Token Balances
                    </h3>
                    {!onChainBalances?.solana_wallet && (
                      <Button onClick={setupTokenAccounts} size="sm" variant="outline">
                        Setup Token Accounts
                      </Button>
                    )}
                  </div>
                  
                  {onChainBalances?.solana_wallet ? (
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* GSG Token */}
                      <div className="p-4 rounded-lg bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Coins weight="fill" className="w-6 h-6 text-yellow-600" />
                          <span className="font-medium">GSG Token</span>
                          <Badge variant="outline" className="text-xs ml-auto">Loyalty</Badge>
                        </div>
                        <p className="text-3xl font-bold text-yellow-700">
                          {onChainBalances?.on_chain?.gsg_minted_total?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-yellow-600 mt-1">GinsGoodCoin</p>
                      </div>

                      {/* GCC Token */}
                      <div className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Leaf weight="fill" className="w-6 h-6 text-green-600" />
                          <span className="font-medium">GCC Token</span>
                          <Badge variant="outline" className="text-xs ml-auto">Carbon</Badge>
                        </div>
                        <p className="text-3xl font-bold text-green-700">
                          {onChainBalances?.on_chain?.gcc_minted_total?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          = {onChainBalances?.on_chain?.gcc_minted_total || 0} kg CO2 offset capacity
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No Solana wallet connected.</p>
                      <p className="text-sm">Click "Setup Token Accounts" to create your wallet.</p>
                    </div>
                  )}

                  {onChainBalances?.solana_wallet && (
                    <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
                      <p>Wallet: {onChainBalances.solana_wallet}</p>
                      <p>Network: Solana Devnet</p>
                    </div>
                  )}
                </Card>

                {/* Exchange GSG to GCC */}
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <ArrowsClockwise weight="duotone" className="w-5 h-5 text-blue-500" />
                    Exchange GSG → GCC
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Convert your GSG tokens to GCC (Carbon Credits). Rate: 1000 GSG = 1 GCC (1 kg CO2)
                  </p>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="text-sm font-medium">Amount (GSG)</label>
                      <input
                        type="number"
                        min="100"
                        value={exchangeAmount}
                        onChange={(e) => setExchangeAmount(e.target.value)}
                        placeholder="Min: 100"
                        className="w-full mt-1 px-3 py-2 border rounded-md"
                        data-testid="exchange-input"
                      />
                    </div>
                    <div className="text-center px-4">
                      <ArrowsClockwise className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium">You'll receive (GCC)</label>
                      <div className="mt-1 px-3 py-2 bg-muted rounded-md text-lg font-bold">
                        {exchangeAmount ? (parseFloat(exchangeAmount) / 1000).toFixed(3) : '0'} GCC
                      </div>
                    </div>
                    <Button onClick={handleExchange} data-testid="exchange-btn">
                      Exchange
                    </Button>
                  </div>
                </Card>

                {/* Retire Carbon Credits */}
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Fire weight="duotone" className="w-5 h-5 text-red-500" />
                    Retire Carbon Credits
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Permanently retire GCC to offset your carbon footprint. Retired credits are burned and cannot be recovered.
                  </p>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="text-sm font-medium">Amount (GCC)</label>
                      <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={retireAmount}
                        onChange={(e) => setRetireAmount(e.target.value)}
                        placeholder="Amount to retire"
                        className="w-full mt-1 px-3 py-2 border rounded-md"
                        data-testid="retire-input"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium">Carbon Offset</label>
                      <div className="mt-1 px-3 py-2 bg-green-50 rounded-md text-green-700">
                        {retireAmount || 0} kg CO2
                      </div>
                    </div>
                    <Button onClick={handleRetire} variant="destructive" data-testid="retire-btn">
                      Retire & Offset
                    </Button>
                  </div>
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
                    <p className="font-medium mb-1">What happens when you retire:</p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      <li>GCC tokens are permanently burned (destroyed)</li>
                      <li>You receive a carbon offset certificate</li>
                      <li>Transaction is recorded on Solana blockchain</li>
                    </ul>
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Redeem Tab */}
            <TabsContent value="redeem">
              <div className="grid md:grid-cols-2 gap-4">
                {(redemptionOptions || []).map(option => (
                  <Card 
                    key={option.id} 
                    className={`p-6 ${wallet?.balance >= option.cost ? 'hover:shadow-lg cursor-pointer' : 'opacity-60'} transition-all`}
                    data-testid={`redeem-${option.id}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-medium">{option.name}</h4>
                        <p className="text-sm text-muted-foreground">{option.description}</p>
                      </div>
                      <Badge variant={option.type === 'discount' ? 'default' : 'secondary'}>
                        {option.type}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-1 text-lg font-bold">
                        <Coins className="w-5 h-5 text-yellow-500" />
                        {option.cost}
                      </div>
                      <Button 
                        size="sm"
                        disabled={wallet?.balance < option.cost}
                        onClick={() => handleRedeem(option.id, option.cost)}
                      >
                        {wallet?.balance >= option.cost ? 'Redeem' : 'Not Enough'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history">
              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4">Transaction History</h3>
                {(!transactions || transactions.length === 0) ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Coins className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No transactions yet. Start certifying products to earn GSG!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(transactions || []).map(tx => (
                      <div 
                        key={tx.id} 
                        className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                        data-testid={`tx-${tx.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            tx.type === 'earn' ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {tx.type === 'earn' ? (
                              <ArrowUp className="w-5 h-5 text-green-600" />
                            ) : (
                              <ArrowDown className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${tx.type === 'earn' ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.type === 'earn' ? '+' : ''}{tx.amount} GSG
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Balance: {tx.balance_after}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Leaderboard Tab */}
            <TabsContent value="leaderboard">
              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Trophy weight="duotone" className="w-5 h-5 text-yellow-500" />
                  Top GSG Earners
                </h3>
                {(!leaderboard || leaderboard.length === 0) ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No earners yet. Be the first to certify and earn GSG!</p>
                  </div>
                ) : (
                <div className="space-y-3">
                  {(leaderboard || []).map((entry, idx) => (
                    <div 
                      key={entry.owner_id || entry.id || `carbon-leaderboard-${idx}`}
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        entry.owner_id === irisId ? 'bg-primary/10 border border-primary' : 'bg-muted/50'
                      }`}
                      data-testid={`leaderboard-${idx}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                          idx === 1 ? 'bg-gray-100 text-gray-700' :
                          idx === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-muted'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium">{entry.owner_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{entry.owner_type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 font-bold">
                          <Coins className="w-4 h-4 text-yellow-500" />
                          {entry.lifetime_earned?.toLocaleString()}
                        </div>
                        <Badge variant="outline" className="text-xs">{entry.tier}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WalletPage;
